import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import HeroAnimatedIcons from "./HeroAnimatedIcons";
import heroLogo from "../assets/heroImage.webp";
import heroLogo2x from "../assets/heroImage@2x.webp";
import { getDocOnce } from "../utils/firestoreOnce";

export default function NewHeroSection() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const lang = isArabic ? 'ar' : 'en';

  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocOnce('homePage/content')
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists() && snap.data().hero) setHeroData(snap.data().hero);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const mainTitle = heroData?.mainTitle?.[lang] || t('hero.mainTitle');
  const mainDescription = heroData?.mainDescription?.[lang] || t('hero.mainDescription');
  const exploreMore = heroData?.exploreMore?.[lang] || t('hero.exploreMore');

  return (
    <div className="relative overflow-hidden">
      <div className={`items-center w-10/12 grid-cols-1 lg:grid-cols-2 mx-auto overflow-x-hidden lg:grid md:py-14 lg:py-24 xl:py-14 lg:mt-3 xl:mt-5 relative z-10`}>

        <div className={`md:mb-14 py-14 md:py-0 relative z-10 flex flex-col items-center text-center`}>
          {/* Decorative badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 mb-6 rounded-full bg-[#0F172A] border border-[#FAA617]/20 shadow-md">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FAA617] animate-pulse"></span>
            <span className="text-sm md:text-base font-bold text-[#FAA617] tracking-wide">{isArabic ? "ومعاً نصنع فرقاً حقيقياً" : "Together we make a real difference"}</span>
          </div>

          <h1 className={`text-4xl font-extrabold text-gray-900 dark:text-white xl:text-6xl lg:text-5xl leading-[1.4] tracking-tight`}>
            <span className="block w-full">{mainTitle.split(' ').slice(0, -1).join(' ')}</span>
            <span className="block w-full text-primary dark:text-[#FAA617] mt-3">{mainTitle.split(' ').slice(-1).join(' ')}</span>
          </h1>

          <p className={`py-6 text-lg md:text-xl text-gray-600 dark:text-gray-400 2xl:py-8 md:py-6 leading-relaxed max-w-2xl w-full text-justify [text-align-last:center]`} dir={isArabic ? 'rtl' : 'ltr'}>
            {mainDescription}
          </p>

          <div className="mt-4">
            <Link
              to="/about"
              className="px-8 py-3.5 text-lg font-bold tracking-wider text-white bg-primary dark:bg-[#0D6EFD] rounded-xl hover:bg-primary-light dark:hover:bg-blue-600 transition-all duration-300 inline-block group transform hover:-translate-y-1 shadow-lg dark:shadow-[0_4px_20px_rgba(13,110,253,0.3)] hover:shadow-xl dark:hover:shadow-[0_8px_30px_rgba(13,110,253,0.4)]"
            >
              <span className="flex items-center gap-3">
                {exploreMore}
                <svg className={`w-5 h-5 text-white transition-transform duration-300 ${isArabic ? 'group-hover:-translate-x-2 rotate-180' : 'group-hover:translate-x-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </span>
            </Link>
          </div>
        </div>

        <div className={`pb-10 md:p-10 lg:p-0 sm:pb-0 flex justify-center items-center h-full`}>
          {/* Light mode: animated icons | Dark mode: logo image */}
          <div className="block dark:hidden w-full">
            <HeroAnimatedIcons />
          </div>
          <div className="hidden dark:flex flex-col justify-center items-center w-full relative py-12 lg:py-4 h-full min-h-[400px] lg:min-h-[500px]">
            {/* Ambient Background Glows */}
            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
               <motion.div 
                 className="absolute w-[300px] h-[300px] bg-[#0D6EFD]/20 rounded-full blur-[100px]"
                 animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
                 transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
               />
               <motion.div 
                 className="absolute w-[250px] h-[250px] bg-[#FAA617]/10 rounded-full blur-[80px]"
                 animate={{ scale: [1.2, 0.9, 1.2], opacity: [0.5, 0.2, 0.5] }}
                 transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
               />
            </div>

            {/* The Floating Image */}
            <motion.img 
              src={heroLogo}
              srcSet={`${heroLogo} 1x, ${heroLogo2x} 2x`}
              alt="EMA-X solutions" 
              width="550"
              height="367"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full max-w-[450px] lg:max-w-[550px] h-auto object-contain relative z-10 drop-shadow-[0_15px_30px_rgba(13,110,253,0.2)]"
              animate={{ 
                y: [-20, 15, -20],
                rotateZ: [-1, 1, -1]
              }}
              transition={{ 
                duration: 6, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
            
            {/* The Glowing Shadow Floor */}
            <motion.div 
              className="absolute bottom-8 lg:bottom-10 w-[60%] max-w-[350px] h-[15px] bg-[#0D6EFD] rounded-[100%] blur-[25px] z-0"
              animate={{
                scale: [0.7, 1.1, 0.7],
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </div>

      </div>
    </div>
  );
}
